import { createContext, useCallback, useState, useMemo, useEffect, useContext } from 'react';
import type { ReactNode } from 'react';
import { message, Modal } from 'antd';
import { noop, get, flatten, map, find } from 'lodash-es';
import { useTranslate } from '@tolgee/react';
import { useFormikContext } from 'formik';
import { currentSpuList } from '@knowledge/knowledges/singleClass/state';
import { splitSkuApart } from '@knowledge/common/helpers/sku';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';
import type { SpuSelectMap } from '@knowledge/common/helpers/sku';
import createNamedContext from 'utils/createNamedContext';
import { useAtomValue } from 'jotai';

export const CurrentKnowledgeValueContext = createContext({
  active: '',
  setActive: noop,
});

export const CurrentKnowledgeValueProvider = ({ children }: { children: ReactNode }) => {
  const [currentKnowledgeValue, setCurrentKnowledgeValue] = useState<string>('');
  const setActive = useCallback((active: string) => {
    setCurrentKnowledgeValue(active);
  }, []);

  return (
    <CurrentKnowledgeValueContext.Provider value={{ active: currentKnowledgeValue, setActive }}>
      {children}
    </CurrentKnowledgeValueContext.Provider>
  );
};

export const FilterBarValueContext = createContext({
  keyword: '',
  keywordType: 1,
  setKeyword: noop,
  setKeywordType: noop,
  storeScopes: [] as General.StoreScope[],
  setStoreScopes: noop,
});

interface FilterBarValueProviderProps {
  children: ReactNode;
  defaultKeyword?: string;
  defaultKeywordType?: number;
}
export const FilterBarValueProvider = (props: FilterBarValueProviderProps) => {
  const { children, defaultKeyword, defaultKeywordType } = props;
  const [keyword, setKeyword] = useState<string>(defaultKeyword || '');
  const [keywordType, setKeywordType] = useState<number>(defaultKeywordType || 1);
  const [storeScopes, setStoreScopes] = useState<General.StoreScope[]>([]);

  return (
    <FilterBarValueContext.Provider
      value={{ keyword, keywordType, setKeywordType, setKeyword, storeScopes, setStoreScopes }}
    >
      {children}
    </FilterBarValueContext.Provider>
  );
};

export const SpuClassDirtyContext = createContext<{
  value: Record<string, boolean>;
  registerDirty: (id: string, dirty: boolean) => void;
  clearDirty: () => void;
  isDirty: boolean;
  wrapDirtyConfirm: (fn: (...arg: any[]) => void) => (...args: any[]) => void;
}>({
  value: {},
  registerDirty: noop,
  clearDirty: noop,
  isDirty: false,
  wrapDirtyConfirm: () => noop,
});

export const SpuClassDirtyContextProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslate();
  const [dirtyContent, setDirtyContent] = useState<Record<string, boolean>>({});
  const registerDirty = useCallback((id: string, dirty: boolean) => {
    setDirtyContent((prev) => {
      return {
        ...prev,
        [id]: dirty,
      };
    });
  }, []);
  const isDirty = useMemo(() => {
    const d = Object.values(dirtyContent);
    return d.length > 0 && d.some((v) => v);
  }, [dirtyContent]);

  const wrapDirtyConfirm = useCallback(
    (fn?: any) => {
      return (...args: any[]) => {
        // 这里查询下是否有打开知识编辑，未关闭的
        const notClose = document.querySelectorAll('.knowledge-content-item');
        if (!isDirty && notClose.length === 0) {
          fn?.(...args);
          setDirtyContent({});
          return;
        }
        Modal.confirm({
          title: t('knowledge.exitTip'),
          onOk: () => {
            fn?.(...args);
            setDirtyContent({});
          },
        });
      };
    },
    [isDirty, t],
  );

  return (
    <SpuClassDirtyContext.Provider
      value={{
        value: dirtyContent,
        isDirty,
        registerDirty: registerDirty,
        clearDirty: () => setDirtyContent({}),
        wrapDirtyConfirm,
      }}
    >
      {children}
    </SpuClassDirtyContext.Provider>
  );
};

export const ClassActiveKnowledgeIdContext = createContext({
  activeId: '',
});

/**
 * 未配置的sku列表
 */
export const UnConfigSkuItem = createContext<ItemSelectV2.Item[]>([]);
export const UnConfigSkuItemProvider = ({ children }: { children: ReactNode }) => {
  const [list, setList] = useState<ItemSelectV2.Item[]>([]);
  const spus = useAtomValue(currentSpuList);
  const { initialValues: values } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  useEffect(() => {
    const other = map(flatten(map(values.values, (v) => v.spus)), (each) => {
      const { itemInternalId, skus } = each!;
      return {
        internalItemId: itemInternalId,
        skuInfo: skus,
      };
    });
    setList(splitSkuApart(spus, other) as ItemSelectV2.Item[]);
  }, [spus, values]);

  return <UnConfigSkuItem.Provider value={list}>{children}</UnConfigSkuItem.Provider>;
};

/**
 * 已配置的sku列表
 */
export const ConfigSkuItem = createContext<ItemSelectV2.Item[]>([]);
export const ConfigSkuItemProvider = ({ children }: { children: ReactNode }) => {
  const [list, setList] = useState<ItemSelectV2.Item[]>([]);
  const { initialValues: values } = useFormikContext<Knowledge.SingleClass.Knowledge>();
  const { active } = useContext(CurrentKnowledgeValueContext);
  const { t } = useTranslate();
  const items = useMemo(() => {
    const target = find(values.values, { id: active });
    return get(target, `spus`, []);
  }, [values, active]);
  useEffect(() => {
    getItemsByInternalIds(items)
      .then((res) => {
        setList(res);
      })
      .catch(() => {
        message.error(t('phrase.获取数据失败，请稍后再试'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return <ConfigSkuItem.Provider value={list}>{children}</ConfigSkuItem.Provider>;
};

/**
 * 同步checkbox select状态
 */

type SpuSelectMapCache = {
  unConfig: SpuSelectMap;
  config: SpuSelectMap;
};
export const SyncCheckboxSelect = createNamedContext<{
  data: SpuSelectMapCache;
  syncMap: (key: keyof SpuSelectMapCache, map: SpuSelectMap) => void;
  isParent: boolean;
}>('SyncCheckboxSelect', null!);
export const SyncCheckboxSelectProvider = ({ children }: { children: ReactNode }) => {
  const [map, setMap] = useState<SpuSelectMapCache>({} as SpuSelectMapCache);
  const parent = useContext(SyncCheckboxSelect);
  const data = parent ? parent.data : map;
  const syncMap = parent
    ? noop
    : (key: keyof SpuSelectMapCache, value: SpuSelectMap) => {
        setMap((prev) => {
          return {
            ...prev,
            [key]: value,
          };
        });
      };

  return (
    <SyncCheckboxSelect.Provider value={{ data, syncMap, isParent: !parent }}>
      {children}
    </SyncCheckboxSelect.Provider>
  );
};

/**
 * 扩大SKU配置区域关闭按钮
 */
export const CloseEditSkuButtonContext = createNamedContext<{
  onClose: () => void;
}>('CloseEditSkuButton', {
  onClose: noop,
});

export const CloseEditSkuButtonProvider = ({
  children,
  onClose = noop,
}: {
  children: ReactNode;
  onClose: () => void;
}) => {
  return (
    <CloseEditSkuButtonContext.Provider value={{ onClose }}>
      {children}
    </CloseEditSkuButtonContext.Provider>
  );
};
